/**
 * How many guests an object may hold — the admin-UI copy.
 *
 * **This is not the rule. It is the explanation of the rule.** The rule is
 * enforced behind the API (ADR-0020) in:
 *   - `backend/src/core/utils/guestCapacity.ts` — the booking write guards,
 *   - `new-backend/admin/src/lib/booking/guestCapacity.ts` — the object-side
 *     guard on a capacity reduction,
 *   - `GroupPGService.findAvailableApartments` (SQL) — which objects are
 *     offered for a requested guest count.
 *
 * This copy exists so the operator learns about the limit while typing rather
 * than from a 400 after pressing Save, and so the message can say what to do
 * instead — enter the maximum and record the real party size in the comment.
 * `guestCapacity.test.ts` pins it against the same cases the backend suites use.
 *
 * Two consequences of being the explanation and not the rule:
 *   - **Absent capacity means unrestricted**, exactly as the backend reads it.
 *   - **Unknown capacity never blocks.** The capacity field is not declared on
 *     every response the booking screens read, so when it is missing this
 *     simply does not warn and the backend still refuses the save. A missing
 *     warning is a worse message; it is never a wrong save.
 */

export type AccommodationCapacity = {
    guests?: number | string | null;
} | null | undefined;

/**
 * The configured guest capacity, or `null` when the object carries none — or
 * when the response did not include one, which is indistinguishable here and
 * treated the same way on purpose.
 */
export const guestCapacityOf = (
    accomodationdetail: AccommodationCapacity | unknown,
): number | null => {
    if (!accomodationdetail || typeof accomodationdetail !== 'object') return null;
    const raw = (accomodationdetail as Record<string, unknown>).guests;
    if (raw === null || raw === undefined || raw === '') return null;
    const capacity = typeof raw === 'number' ? raw : Number(raw);
    if (!Number.isFinite(capacity) || capacity <= 0) return null;
    return Math.floor(capacity);
};

/**
 * Whether this guest count exceeds what the object holds. False whenever
 * either side is unknown — see the note on blocking above.
 */
export const exceedsGuestCapacity = (
    accomodationdetail: AccommodationCapacity | unknown,
    guests: number | null | undefined,
): boolean => {
    const capacity = guestCapacityOf(accomodationdetail);
    if (capacity === null) return false;
    if (guests === null || guests === undefined) return false;
    const requested = Number(guests);
    if (!Number.isFinite(requested) || requested <= 0) return false;
    return requested > capacity;
};

/**
 * Capacity of the selected object, from whichever response carried it.
 *
 * The object-details response declares the field; the availability row is
 * produced by a query that selects it but whose frontend type does not list
 * it. Reading both means the warning works on whichever arrives first, and on
 * either alone.
 */
export const selectedObjectCapacity = (
    details: unknown,
    listRow: unknown,
): number | null =>
    guestCapacityOf((details as { accomodationdetail?: unknown })?.accomodationdetail)
    ?? guestCapacityOf((listRow as { accomodationdetail?: unknown })?.accomodationdetail);
